import { useState } from 'react';
import { useStore } from '@/lib/store';
import type { FeatureItem, TestimonialItem, PricingTier, FAQItem } from '@/lib/api';

const sections = [
  { id: 'hero', name: 'Hero Section', description: 'Large headline with call-to-action', required: true },
  { id: 'features', name: 'Features', description: 'Highlight your key benefits' },
  { id: 'testimonials', name: 'Testimonials', description: 'Customer reviews and social proof' },
  { id: 'pricing', name: 'Pricing', description: 'Pricing table or packages' },
  { id: 'faq', name: 'FAQ', description: 'Frequently asked questions' },
  { id: 'contact', name: 'Contact Form', description: 'Capture leads with a contact form', required: true },
];

export function StepThree() {
  const { formData, setFormData } = useStore();
  const selectedSections = formData.sections || ['hero', 'features', 'contact'];
  const sectionDetails = formData.sectionDetails || {};
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section?.required) return;

    const updated = selectedSections.includes(sectionId)
      ? selectedSections.filter((id) => id !== sectionId)
      : [...selectedSections, sectionId];

    setFormData({ sections: updated });
  };

  const toggleExpanded = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const updateSectionDetails = (sectionId: string, details: Record<string, unknown>) => {
    setFormData({
      sectionDetails: {
        ...sectionDetails,
        [sectionId]: {
          ...(sectionDetails as Record<string, Record<string, unknown>>)[sectionId],
          ...details,
        },
      },
    });
  };

  // Hero section handlers
  const heroDetails = sectionDetails.hero || { headline: '', subheadline: '', ctaText: '' };

  // Features handlers
  const featuresDetails = sectionDetails.features || { sectionTitle: '', items: [{ title: '', description: '' }] };
  const addFeature = () => {
    updateSectionDetails('features', {
      items: [...(featuresDetails.items || []), { title: '', description: '' }],
    });
  };
  const updateFeature = (index: number, field: keyof FeatureItem, value: string) => {
    const items = [...(featuresDetails.items || [])];
    items[index] = { ...items[index], [field]: value };
    updateSectionDetails('features', { items });
  };
  const removeFeature = (index: number) => {
    const items = (featuresDetails.items || []).filter((_, i) => i !== index);
    updateSectionDetails('features', { items: items.length ? items : [{ title: '', description: '' }] });
  };

  // Testimonials handlers
  const testimonialsDetails = sectionDetails.testimonials || { sectionTitle: '', items: [{ name: '', role: '', quote: '' }] };
  const addTestimonial = () => {
    updateSectionDetails('testimonials', {
      items: [...(testimonialsDetails.items || []), { name: '', role: '', quote: '' }],
    });
  };
  const updateTestimonial = (index: number, field: keyof TestimonialItem, value: string) => {
    const items = [...(testimonialsDetails.items || [])];
    items[index] = { ...items[index], [field]: value };
    updateSectionDetails('testimonials', { items });
  };
  const removeTestimonial = (index: number) => {
    const items = (testimonialsDetails.items || []).filter((_, i) => i !== index);
    updateSectionDetails('testimonials', { items: items.length ? items : [{ name: '', role: '', quote: '' }] });
  };

  // Pricing handlers
  const pricingDetails = sectionDetails.pricing || { sectionTitle: '', tiers: [{ name: '', price: '', period: '/month', features: [''], highlighted: false }] };
  const addPricingTier = () => {
    updateSectionDetails('pricing', {
      tiers: [...(pricingDetails.tiers || []), { name: '', price: '', period: '/month', features: [''], highlighted: false }],
    });
  };
  const updatePricingTier = (index: number, field: keyof PricingTier, value: string | string[] | boolean) => {
    const tiers = [...(pricingDetails.tiers || [])];
    tiers[index] = { ...tiers[index], [field]: value };
    updateSectionDetails('pricing', { tiers });
  };
  const removePricingTier = (index: number) => {
    const tiers = (pricingDetails.tiers || []).filter((_, i) => i !== index);
    updateSectionDetails('pricing', { tiers: tiers.length ? tiers : [{ name: '', price: '', period: '/month', features: [''], highlighted: false }] });
  };

  // FAQ handlers
  const faqDetails = sectionDetails.faq || { sectionTitle: '', items: [{ question: '', answer: '' }] };
  const addFAQ = () => {
    updateSectionDetails('faq', {
      items: [...(faqDetails.items || []), { question: '', answer: '' }],
    });
  };
  const updateFAQ = (index: number, field: keyof FAQItem, value: string) => {
    const items = [...(faqDetails.items || [])];
    items[index] = { ...items[index], [field]: value };
    updateSectionDetails('faq', { items });
  };
  const removeFAQ = (index: number) => {
    const items = (faqDetails.items || []).filter((_, i) => i !== index);
    updateSectionDetails('faq', { items: items.length ? items : [{ question: '', answer: '' }] });
  };

  // Contact handlers
  const contactDetails = sectionDetails.contact || { sectionTitle: '', subtitle: '' };

  return (
    <div className="space-y-6 animate-in">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Content & Features</h2>
        <p className="text-gray-600">Choose sections and provide details for your landing page.</p>
      </div>

      <div className="space-y-3">
        {sections.map((section) => {
          const isSelected = selectedSections.includes(section.id);
          const isExpanded = expandedSection === section.id && isSelected;

          return (
            <div key={section.id} className="border-2 rounded-xl overflow-hidden transition-all duration-300" style={{ borderColor: isSelected ? '#3f51b5' : '#e5e7eb' }}>
              {/* Section header */}
              <button
                type="button"
                disabled={section.required && !isSelected}
                className={`
                  w-full p-4 text-left transition-all flex items-center justify-between
                  ${isSelected ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'}
                  ${section.required ? 'cursor-default' : 'cursor-pointer'}
                `}
                onClick={() => !section.required && toggleSection(section.id)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                      ${isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-300'}
                    `}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {section.name}
                      {section.required && (
                        <span className="ml-2 text-xs text-primary-600 font-normal">(Required)</span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-500">{section.description}</p>
                  </div>
                </div>
                {isSelected && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(section.id);
                    }}
                    className="px-3 py-1 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    {isExpanded ? 'Hide Details' : 'Add Details'}
                    <svg
                      className={`w-4 h-4 ml-1 inline-block transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                )}
              </button>

              {/* Section details (expanded) */}
              {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-4">
                  {/* Hero Details */}
                  {section.id === 'hero' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Leave blank and we'll generate compelling copy for you.</p>
                      <div>
                        <label className="label">Headline</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Transform Your Business Today'"
                          value={heroDetails.headline || ''}
                          onChange={(e) => updateSectionDetails('hero', { headline: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="label">Subheadline</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Join thousands of businesses using our platform'"
                          value={heroDetails.subheadline || ''}
                          onChange={(e) => updateSectionDetails('hero', { subheadline: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="label">Call-to-Action Button Text</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Get Started Free'"
                          value={heroDetails.ctaText || ''}
                          onChange={(e) => updateSectionDetails('hero', { ctaText: e.target.value })}
                        />
                      </div>
                    </>
                  )}

                  {/* Features Details */}
                  {section.id === 'features' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Add your key features or leave blank for AI-generated content.</p>
                      <div>
                        <label className="label">Section Title (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Why Choose Us'"
                          value={featuresDetails.sectionTitle || ''}
                          onChange={(e) => updateSectionDetails('features', { sectionTitle: e.target.value })}
                        />
                      </div>
                      {(featuresDetails.items || []).map((feature, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Feature {index + 1}</span>
                            {(featuresDetails.items || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFeature(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            className="input"
                            placeholder="Feature title"
                            value={feature.title}
                            onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          />
                          <textarea
                            className="input min-h-[80px]"
                            placeholder="Feature description"
                            value={feature.description}
                            onChange={(e) => updateFeature(index, 'description', e.target.value)}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addFeature}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-colors"
                      >
                        + Add Feature
                      </button>
                    </>
                  )}

                  {/* Testimonials Details */}
                  {section.id === 'testimonials' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Add customer testimonials or leave blank for placeholders.</p>
                      <div>
                        <label className="label">Section Title (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'What Our Customers Say'"
                          value={testimonialsDetails.sectionTitle || ''}
                          onChange={(e) => updateSectionDetails('testimonials', { sectionTitle: e.target.value })}
                        />
                      </div>
                      {(testimonialsDetails.items || []).map((testimonial, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Testimonial {index + 1}</span>
                            {(testimonialsDetails.items || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTestimonial(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              className="input"
                              placeholder="Customer name"
                              value={testimonial.name}
                              onChange={(e) => updateTestimonial(index, 'name', e.target.value)}
                            />
                            <input
                              type="text"
                              className="input"
                              placeholder="Role/Company"
                              value={testimonial.role}
                              onChange={(e) => updateTestimonial(index, 'role', e.target.value)}
                            />
                          </div>
                          <textarea
                            className="input min-h-[80px]"
                            placeholder="Their testimonial quote"
                            value={testimonial.quote}
                            onChange={(e) => updateTestimonial(index, 'quote', e.target.value)}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addTestimonial}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-colors"
                      >
                        + Add Testimonial
                      </button>
                    </>
                  )}

                  {/* Pricing Details */}
                  {section.id === 'pricing' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Define your pricing tiers or leave blank for a generic pricing section.</p>
                      <div>
                        <label className="label">Section Title (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Simple, Transparent Pricing'"
                          value={pricingDetails.sectionTitle || ''}
                          onChange={(e) => updateSectionDetails('pricing', { sectionTitle: e.target.value })}
                        />
                      </div>
                      {(pricingDetails.tiers || []).map((tier, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">Pricing Tier {index + 1}</span>
                            <div className="flex items-center gap-3">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={tier.highlighted || false}
                                  onChange={(e) => updatePricingTier(index, 'highlighted', e.target.checked)}
                                  className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                                />
                                Highlight
                              </label>
                              {(pricingDetails.tiers || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removePricingTier(index)}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                          <input
                            type="text"
                            className="input"
                            placeholder="Tier name (e.g., 'Starter')"
                            value={tier.name}
                            onChange={(e) => updatePricingTier(index, 'name', e.target.value)}
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              className="input"
                              placeholder="Price (e.g., '$29')"
                              value={tier.price}
                              onChange={(e) => updatePricingTier(index, 'price', e.target.value)}
                            />
                            <select
                              className="input"
                              value={tier.period}
                              onChange={(e) => updatePricingTier(index, 'period', e.target.value)}
                            >
                              <option value="/month">/month</option>
                              <option value="/year">/year</option>
                              <option value=" one-time">one-time</option>
                              <option value="">no period</option>
                            </select>
                          </div>
                          <div>
                            <label className="label text-sm">Features (one per line)</label>
                            <textarea
                              className="input min-h-[80px]"
                              placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
                              value={(tier.features || []).join('\n')}
                              onChange={(e) => updatePricingTier(index, 'features', e.target.value.split('\n'))}
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addPricingTier}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-colors"
                      >
                        + Add Pricing Tier
                      </button>
                    </>
                  )}

                  {/* FAQ Details */}
                  {section.id === 'faq' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Add FAQ items or leave blank for AI-generated questions.</p>
                      <div>
                        <label className="label">Section Title (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Frequently Asked Questions'"
                          value={faqDetails.sectionTitle || ''}
                          onChange={(e) => updateSectionDetails('faq', { sectionTitle: e.target.value })}
                        />
                      </div>
                      {(faqDetails.items || []).map((faq, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">FAQ {index + 1}</span>
                            {(faqDetails.items || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeFAQ(index)}
                                className="text-red-500 hover:text-red-700 text-sm"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            className="input"
                            placeholder="Question"
                            value={faq.question}
                            onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                          />
                          <textarea
                            className="input min-h-[80px]"
                            placeholder="Answer"
                            value={faq.answer}
                            onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addFAQ}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-500 hover:text-primary-500 transition-colors"
                      >
                        + Add FAQ
                      </button>
                    </>
                  )}

                  {/* Contact Details */}
                  {section.id === 'contact' && (
                    <>
                      <p className="text-sm text-gray-500 italic">Customize the contact section heading.</p>
                      <div>
                        <label className="label">Section Title (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'Get In Touch'"
                          value={contactDetails.sectionTitle || ''}
                          onChange={(e) => updateSectionDetails('contact', { sectionTitle: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="label">Subtitle (optional)</label>
                        <input
                          type="text"
                          className="input"
                          placeholder="e.g., 'We'd love to hear from you'"
                          value={contactDetails.subtitle || ''}
                          onChange={(e) => updateSectionDetails('contact', { subtitle: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-sm text-gray-500 text-center pt-4">
        Don't worry - any fields left blank will be filled with professionally generated content.
      </p>
    </div>
  );
}
